/*
 * MarineMaker: <mm-quote-request> web component.
 * Section: sections/mm-quote-request.liquid. Spec: handoff/spec/custom-sections.md § 2.
 * Logic and copy ported from handoff/design-reference/Quote.dc.html (defs() / common() / validate())
 * and the preset pages Badges / Registration / CustomAcrylic.dc.html.
 *
 * No frameworks, no external requests. Delivery modes:
 * - endpoint: POST multipart to the section's `endpoint` (App Proxy, Phase 8).
 * - shopify:  no endpoint; the request is copied into the section's native Shopify contact form
 *             (form.mm-quote__shopify-form) and submitted, so Shopify emails it to the store.
 *             Contact forms can't carry files, so file fields are replaced by a reply-with-photos note.
 * - demo:     neither; nothing is sent and the success state says so.
 */
(() => {
  if (window.customElements && customElements.get('mm-quote-request')) return;

  const TYPES = ['name', 'badge', 'registration', 'custom', 'replace'];

  /* ---------- Field definitions (ported from Quote.dc.html defs() / common()) ---------- */
  const FIN = ['Not sure yet', 'Mirror Gold', 'Mirror Silver', 'Gloss Black', 'Gloss White', 'Frosted'];
  const ACCEPT_ART = 'image/*,.pdf,.ai,.eps,.svg';
  const ACCEPT_PHOTO = 'image/*,.pdf';
  // `key` = payload field name. `id` = stable DOM id fragment.
  const boat = (req) => ({ id: 'boat', key: 'boat_model', label: 'Boat make and model', req: !!req, ph: 'e.g. Sea Ray 280' });

  const DEFS = {
    name: {
      title: 'Boat name', short: 'Acrylic or vinyl', heading: 'Boat name or lettering',
      intro: 'For dimensional acrylic or cut vinyl names. Tell us what you have in mind, or attach a design from the studio.',
      submit: 'Send request', success: 'Your boat name request is ready to send.', reset: 'Start another request',
      notesPh: 'Placement, colours, or an existing name to remove.',
      fields: [
        { id: 'studio', key: 'studio_link', label: 'Studio design link', ph: 'Paste your shareable design link', hint: 'Carries your name, style, finish and size into this request.', full: true },
        { id: 'bname', key: 'boat_name', label: 'Boat name, as it should appear', req: true },
        { id: 'port', key: 'hailing_port', label: 'Hailing port' },
        boat(true),
        { id: 'dims', key: 'transom_area', label: 'Transom area available (W × H)', ph: 'e.g. 84 in × 20 in' },
        { id: 'photos', key: 'files', label: 'Photo of your transom', kind: 'file', accept: ACCEPT_PHOTO, full: true, hint: 'A straight-on photo helps most.' }
      ]
    },
    badge: {
      title: 'Badge or emblem', short: 'Badges, monograms', heading: 'Badge or emblem project',
      intro: 'Model badges, emblems, monograms and custom lettering. Send artwork or a photo of the original, and the size you need.',
      submit: 'Send badge brief', success: 'Your badge brief is ready to send.', reset: 'Start another brief',
      notesPh: 'Colours, placement, or anything to match.',
      fields: [
        { id: 'what', key: 'item', label: 'What are we making?', kind: 'select', req: true, opts: ['Choose one', 'Model badge', 'Emblem', 'Monogram', 'Custom lettering', 'Something else'] },
        { id: 'qty', key: 'quantity', label: 'Quantity', type: 'number', req: true, ph: 'e.g. 2', hint: 'One for each side? Tell us the total.' },
        { id: 'art', key: 'files', label: 'Artwork or photo of the original', kind: 'file', accept: ACCEPT_ART, req: true, full: true, hint: 'Vector files (AI, EPS, SVG, PDF) are ideal. Photos and sketches are fine to start.' },
        { id: 'size', key: 'finished_size', label: 'Finished size (W × H)', req: true, ph: 'e.g. 18 in × 6 in' },
        { id: 'finish', key: 'finish', label: 'Finish', kind: 'select', opts: FIN },
        boat(false)
      ]
    },
    registration: {
      title: 'Registration', short: 'Numbers to spec', heading: 'Registration numbers',
      intro: 'We cut registration lettering to the exact characters, height and placement you give us.',
      note: 'Please confirm the requirements that apply to your vessel before ordering. We cut to the text and dimensions you provide.',
      submit: 'Send registration request', success: 'Your registration request is ready to send.', reset: 'Start another request',
      notesPh: 'Colour to match, existing numbers to replace, anything else.',
      confirm: 'I’ve confirmed the characters, height and placement required for my vessel.',
      fields: [
        { id: 'regtext', key: 'characters', label: 'Exact characters', req: true, ph: 'Type exactly as it should appear', full: true, mono: true },
        { id: 'regh', key: 'character_height', label: 'Required character height', req: true, ph: 'e.g. 3 in' },
        { id: 'place', key: 'placement', label: 'Intended placement', kind: 'select', req: true, opts: ['Choose one', 'Both sides of the bow', 'Transom', 'Other (describe in notes)'] },
        { id: 'mat', key: 'material', label: 'Material', kind: 'select', opts: ['Not sure yet', 'Dimensional acrylic', 'Cut vinyl'] },
        { id: 'finish', key: 'finish', label: 'Finish or colour', kind: 'select', opts: FIN },
        boat(false),
        { id: 'photos', key: 'files', label: 'Photo of the placement area', kind: 'file', accept: ACCEPT_PHOTO, full: true }
      ]
    },
    custom: {
      title: 'Custom acrylic', short: 'Plaques, signs', heading: 'Custom acrylic project',
      intro: 'Hailing ports, dock plaques, marine signs and one-off pieces. Tell us where it will go and roughly how big.',
      submit: 'Send project brief', success: 'Your project brief is ready to send.', reset: 'Start another brief',
      notesPh: 'Wording, colours, mounting surface, anything else.',
      fields: [
        { id: 'ptype', key: 'project_type', label: 'Project type', kind: 'select', req: true, opts: ['Choose one', 'Hailing-port lettering', 'Dock plaque', 'Marine sign', 'Something else'] },
        { id: 'where', key: 'location', label: 'Where will it be used?', kind: 'select', opts: ['Choose one', 'On the boat', 'Dock or marina', 'Building or indoors'] },
        { id: 'size', key: 'approx_size', label: 'Approximate size (W × H)', req: true, ph: 'e.g. 24 in × 8 in' },
        { id: 'qty', key: 'quantity', label: 'Quantity', type: 'number', ph: 'e.g. 1' },
        { id: 'finish', key: 'finish', label: 'Finish', kind: 'select', opts: FIN },
        { id: 'art', key: 'files', label: 'Artwork, sketch or reference', kind: 'file', accept: ACCEPT_ART, full: true }
      ]
    },
    replace: {
      title: 'Replacement', short: 'Damaged letters', heading: 'Replacement letter',
      intro: 'Send a photo of the damaged letter and its approximate size. We’ll confirm whether we can match it and quote the replacement.',
      submit: 'Send request', success: 'Your replacement request is ready to send.', reset: 'Start another request',
      notesPh: 'Anything else we should know.',
      fields: [
        { id: 'photos', key: 'files', label: 'Photo of the damaged letter', kind: 'file', accept: ACCEPT_PHOTO, req: true, full: true },
        { id: 'size', key: 'letter_height', label: 'Approximate letter height', req: true, ph: 'e.g. 6 in' },
        { id: 'orig', key: 'original_order', label: 'Original order details', ph: 'Order number or approximate date, if you have it' },
        boat(false)
      ]
    }
  };

  const COMMON_HEAD = [
    { id: 'name', key: 'name', label: 'Your name', req: true, ac: 'name' },
    { id: 'email', key: 'email', label: 'Email', type: 'email', req: true, ac: 'email' },
    { id: 'phone', key: 'phone', label: 'Phone', type: 'tel', ac: 'tel' },
    { id: 'marina', key: 'marina', label: 'Marina or town' }
  ];
  const FULFIL = { id: 'fulfil', key: 'fulfilment', label: 'How would you like to receive it?', opts: [['ship', 'Ship across Canada'], ['install', 'Local installation'], ['undecided', 'Not sure yet']] };
  const INSTALL_NOTE = 'Local installation by A1 Marine Care is available in Georgian Bay, Lake Simcoe and the Trent-Severn.';
  const CONSENT_TEXT = 'I agree to be contacted by MarineMaker about this request.';
  // Shopify email mode (no file uploads).
  const FILES_NOTE = 'Photos or artwork: we’ll reply by email, and you can send them in your reply.';
  const SENT_NOTE = 'We’ll reply by email. If you have photos or artwork, send them in your reply.';

  const MSG = {
    required: 'This field is required.',
    file: 'Please attach at least one file.',
    select: 'Please choose an option.',
    email: 'Enter an email address like name@example.com.',
    consent: 'Please agree to be contacted so we can reply.',
    confirm: 'Please confirm the requirements for your vessel.',
    failTitle: 'We couldn’t send your request.',
    failBody: 'Your details are still here.'
  };
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /* ---------- Studio design object (spec: custom-sections.md › Design object) ---------- */
  const STYLE_LABELS = { transom: 'Transom Serif', deck: 'Deck Sans', commodore: 'Commodore', commodoreItalic: 'Commodore Italic', script: 'Yacht Script' };
  // Acrylic finishes are fixed. Vinyl colours are an open question (OPEN-QUESTIONS #3): ids are
  // accepted if they are plain slugs; the stand-in labels below are shown when they match.
  const FINISH_LABELS = {
    'mirror-gold': 'Mirror Gold', 'mirror-silver': 'Mirror Silver', 'gloss-black': 'Gloss Black', 'gloss-white': 'Gloss White', frosted: 'Frosted',
    'v-black': 'Black', 'v-white': 'White', 'v-gold': 'Metallic Gold', 'v-silver': 'Metallic Silver'
  };
  const ACRYLIC_FINISHES = ['mirror-gold', 'mirror-silver', 'gloss-black', 'gloss-white', 'frosted'];

  const num = (v, min, max) => {
    const n = typeof v === 'number' ? v : (typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN);
    return Number.isFinite(n) && n >= min && n <= max ? Math.round(n * 10) / 10 : null;
  };
  const cleanText = (v, max) => {
    if (typeof v !== 'string') return null;
    const t = v.replace(/[\u0000-\u001F\u007F]/g, '').trim();
    return t && t.length <= max ? t : null;
  };

  /** Parse and validate ?design=. Returns a clean object or null. Bad values are dropped. */
  function parseDesign(raw) {
    if (!raw || raw.length > 2000) return null;
    let obj = null;
    const tries = [raw];
    try { tries.push(decodeURIComponent(raw)); } catch (e) { /* ignore */ }
    for (const s of tries) {
      try { obj = JSON.parse(s); break; } catch (e) { obj = null; }
    }
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
    const name = cleanText(obj.name, 18);
    if (!name) return null;
    const d = { name };
    const port = cleanText(obj.port, 28);
    if (port) d.port = port;
    if (obj.material === 'acrylic' || obj.material === 'vinyl') d.material = obj.material;
    if (typeof obj.style === 'string' && Object.prototype.hasOwnProperty.call(STYLE_LABELS, obj.style)) d.style = obj.style;
    if (typeof obj.finish === 'string' && /^[a-z0-9-]{1,32}$/.test(obj.finish)) {
      const okForMaterial = d.material === 'vinyl' ? !ACRYLIC_FINISHES.includes(obj.finish) : (d.material === 'acrylic' ? ACRYLIC_FINISHES.includes(obj.finish) : true);
      if (okForMaterial) d.finish = obj.finish;
    }
    const h = num(obj.height_in, 3, 14); if (h !== null) d.height_in = h;
    const t = num(obj.transom_in, 36, 240); if (t !== null) d.transom_in = t;
    const w = num(obj.approx_width_in, 0.1, 1000); if (w !== null) d.approx_width_in = w;
    return d;
  }

  /* ---------- DOM helper ---------- */
  function h(tag, attrs, ...kids) {
    const el = document.createElement(tag);
    if (attrs) {
      for (const k of Object.keys(attrs)) {
        const v = attrs[k];
        if (v === null || v === undefined || v === false) continue;
        if (k === 'class') el.className = v;
        else if (k === 'text') el.textContent = v;
        else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2), v);
        else el.setAttribute(k, v === true ? '' : String(v));
      }
    }
    for (const kid of kids.flat(Infinity)) {
      if (kid === null || kid === undefined || kid === false) continue;
      el.appendChild(typeof kid === 'string' ? document.createTextNode(kid) : kid);
    }
    return el;
  }

  const reducedMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class MMQuoteRequest extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      const ds = this.dataset;
      this.uid = ds.uid || ('mmq-' + Math.random().toString(36).slice(2, 8));
      this.preset = TYPES.includes(ds.preset) ? ds.preset : 'name';
      this.showPicker = ds.picker === 'true';
      this.maxMb = Math.max(1, Number(ds.maxFileMb) || 20);
      this.studioUrl = ds.studioUrl || '';
      this.typeHeadingLevel = ds.headingLevel === '3' ? 'h3' : 'h2';
      this.endpoint = this.resolveEndpoint(ds.endpoint || '');
      this.contactForm = this.endpoint ? null : this.querySelector('form.mm-quote__shopify-form');
      this.mode = this.endpoint ? 'endpoint' : (this.contactForm ? 'shopify' : 'demo');
      this.demo = this.mode === 'demo';
      this.editor = !!(window.Shopify && window.Shopify.designMode);
      this.simFail = false;
      this.status = 'idle';

      // URL parameters (quote page): ?type= and ?design=
      let params;
      try { params = new URLSearchParams(window.location.search); } catch (e) { params = new URLSearchParams(); }
      const urlType = params.get('type');
      // A studio design only applies where a boat-name form can be shown.
      this.design = (this.showPicker || this.preset === 'name') ? parseDesign(params.get('design')) : null;
      this.type = this.preset;
      if (this.showPicker) {
        if (TYPES.includes(urlType)) this.type = urlType;
        else if (this.design) this.type = 'name';
      }

      this.root = this.querySelector('[data-mmq-root]') || this;
      this.render();
    }

    /** Only same-origin paths ("/apps/…") or https URLs are used. Anything else falls back to demo mode. */
    resolveEndpoint(raw) {
      const v = String(raw).trim();
      if (!v) return '';
      if (/^\/(?!\/)/.test(v)) return v;
      if (/^https:\/\/[^\s]+$/i.test(v)) return v;
      if (window.console) console.warn('[mm-quote-request] Ignoring endpoint that is not a "/path" or https URL; running in demo mode.');
      return '';
    }

    fid(s) { return this.uid + '-' + s; }

    /* ---------- Render ---------- */
    render() {
      const root = this.root;
      root.querySelectorAll('noscript').forEach((n) => n.remove());
      root.textContent = '';

      // Live status for screen readers (loading / result announcements)
      this.liveEl = h('p', { class: 'visually-hidden mm-quote__live', 'aria-live': 'polite', role: 'status' });

      // Project-type picker
      if (this.showPicker) {
        const lblId = this.fid('picker-label');
        this.pickerBtns = {};
        const grid = h('div', { class: 'mm-quote__types', role: 'group', 'aria-labelledby': lblId });
        TYPES.forEach((t) => {
          const d = DEFS[t];
          const b = h('button', { type: 'button', class: 'mm-quote__type', 'aria-pressed': String(t === this.type), 'data-type': t, onclick: () => this.setType(t, true) },
            h('strong', { class: 'mm-quote__type-title', text: d.title }), h('span', { class: 'mm-quote__type-short', text: d.short }));
          this.pickerBtns[t] = b;
          grid.appendChild(b);
        });
        root.appendChild(h('div', { class: 'mm-quote__picker' }, h('p', { class: 'mm-quote__legend', id: lblId, text: 'What’s your project?' }), grid));

        // Per-type heading, intro, studio link and note
        this.typeHeadingEl = h(this.typeHeadingLevel, { class: 'mm-quote__type-heading', id: this.fid('type-heading'), tabindex: '-1' });
        this.typeIntroEl = h('p', { class: 'mm-quote__type-intro' });
        this.typeNoteEl = h('p', { class: 'mm-quote__note', hidden: true });
        this.studioLinkEl = this.studioUrl ? h('a', { class: 'mm-quote__studio-link', href: this.studioUrl, hidden: true, text: 'Design it in the studio first →' }) : null;
        root.appendChild(h('div', { class: 'mm-quote__type-head' }, this.typeHeadingEl, this.typeIntroEl, this.studioLinkEl, this.typeNoteEl));
      }

      // Success panel
      this.successEl = h('div', { class: 'mm-quote__success', hidden: true });
      root.appendChild(this.successEl);

      // Form
      const form = h('form', { class: 'mm-quote__form', novalidate: true, 'aria-label': 'Quote form', onsubmit: (e) => { e.preventDefault(); this.submit(); } });
      this.form = form;

      this.failEl = h('div', { class: 'mm-quote__fail', role: 'alert', hidden: true });
      this.summaryEl = h('div', { class: 'mm-quote__summary', role: 'alert', tabindex: '-1', hidden: true });
      form.append(this.failEl, this.summaryEl);

      this.designEl = this.design ? this.renderDesign() : null;
      if (this.designEl) form.appendChild(this.designEl);

      form.appendChild(h('p', { class: 'mm-quote__req-note' }, h('span', { 'aria-hidden': 'true', text: '* ' }), 'Required field'));

      const grid = h('div', { class: 'mm-quote__grid' });
      const gl = this.showPicker && this.typeHeadingLevel === 'h3' ? 'h4' : 'h3';
      grid.appendChild(h(gl, { class: 'mm-quote__group-title', text: 'Your details' }));
      this.controls = {}; // key: scope:id -> { def, el, wrap }
      COMMON_HEAD.forEach((fl) => grid.appendChild(this.renderField(fl, 'common')));
      grid.appendChild(h(gl, { class: 'mm-quote__group-title', text: 'Your project' }));

      this.typeGroups = {};
      const typesToBuild = this.showPicker ? TYPES : [this.type];
      typesToBuild.forEach((t) => {
        const g = h('div', { class: 'mm-quote__typegroup', 'data-type': t, hidden: t !== this.type });
        const fields = this.fieldsFor(t);
        fields.forEach((fl) => g.appendChild(this.renderField(fl, t)));
        if (fields.length < DEFS[t].fields.length) {
          g.appendChild(h('p', { class: 'mm-quote__hint mm-quote__field--full mm-quote__files-note', text: FILES_NOTE }));
        }
        if (t === 'registration') g.appendChild(this.renderRegPreview());
        this.typeGroups[t] = g;
        grid.appendChild(g);
      });

      grid.appendChild(this.renderFulfil());
      this.notesDef = { id: 'notes', key: 'notes', label: 'Project notes', kind: 'area', full: true, ph: DEFS[this.type].notesPh };
      grid.appendChild(this.renderField(this.notesDef, 'common'));
      form.appendChild(grid);

      // Checkboxes: registration confirmation + consent
      const checks = h('div', { class: 'mm-quote__checks' });
      this.confirmWrap = this.renderCheck('confirm', DEFS.registration.confirm);
      this.consentWrap = this.renderCheck('consent', CONSENT_TEXT);
      checks.append(this.confirmWrap, this.consentWrap);
      form.appendChild(checks);

      // Actions
      this.submitBtn = h('button', { type: 'submit', class: 'btn btn--primary mm-quote__submit' });
      const actions = h('div', { class: 'mm-quote__actions' }, this.submitBtn);
      if (this.editor) {
        this.simFailInput = h('input', { type: 'checkbox', id: this.fid('simfail'), onchange: (e) => { this.simFail = e.target.checked; } });
        actions.appendChild(h('label', { class: 'mm-quote__simfail', for: this.fid('simfail') }, this.simFailInput, ' Demo: simulate a failed send'));
      }
      form.appendChild(actions);
      if (this.demo) {
        form.appendChild(h('p', { class: 'mm-quote__demo-note' },
          h('strong', { text: 'Demo mode. ' }), 'Submitting does not send email, upload files or store data.'));
      }

      root.append(form, this.liveEl);
      this.applyType(false);

      // Back from a Shopify contact-form post: show the result.
      if (this.mode === 'shopify') {
        const errs = this.querySelector('[data-mmq-errors]');
        if (this.querySelector('[data-mmq-posted]')) this.finish(true, '');
        else if (errs) this.finish(false, '', errs.textContent.trim());
      }
    }

    /** Fields for a type; file inputs are dropped when sending through Shopify's contact form. */
    fieldsFor(t) {
      return DEFS[t].fields.filter((fl) => this.mode !== 'shopify' || fl.kind !== 'file');
    }

    renderField(fl, scope) {
      const kind = fl.kind || 'input';
      const id = this.fid((scope === 'common' ? '' : scope + '-') + fl.id);
      const hintId = id + '-hint';
      const errId = id + '-err';
      const label = h('label', { class: 'mm-quote__label', for: id }, fl.label,
        fl.req ? h('span', { class: 'mm-quote__req', 'aria-hidden': 'true', text: ' *' }) : h('span', { class: 'mm-quote__opt', text: ' (optional)' }));
      const common = { id, name: fl.key, 'aria-describedby': hintId + ' ' + errId, 'aria-required': fl.req ? 'true' : null };
      let el;
      if (kind === 'select') {
        el = h('select', Object.assign({ class: 'mm-quote__input mm-quote__select' }, common),
          fl.opts.map((o) => h('option', { value: o === 'Choose one' ? '' : o, text: o })));
      } else if (kind === 'area') {
        el = h('textarea', Object.assign({ class: 'mm-quote__input', rows: '4', placeholder: fl.ph || null }, common));
      } else if (kind === 'file') {
        el = h('input', Object.assign({ class: 'mm-quote__input mm-quote__file', type: 'file', multiple: true, accept: fl.accept || ACCEPT_PHOTO }, common));
      } else {
        el = h('input', Object.assign({
          class: 'mm-quote__input' + (fl.mono ? ' mm-quote__input--mono' : ''), type: fl.type || 'text',
          autocomplete: fl.ac || 'off', placeholder: fl.ph || null,
          inputmode: fl.type === 'number' ? 'numeric' : null, min: fl.type === 'number' ? '1' : null, step: fl.type === 'number' ? '1' : null,
          spellcheck: fl.mono ? 'false' : null, autocapitalize: fl.mono ? 'characters' : null
        }, common));
      }
      const hint = h('span', { class: 'mm-quote__hint', id: hintId, text: fl.hint || '' });
      const err = h('span', { class: 'mm-quote__err', id: errId });
      const wrap = h('div', { class: 'mm-quote__field' + (fl.full ? ' mm-quote__field--full' : '') }, label, el, hint, err);
      const rec = { def: fl, el, wrap, hint, err, scope };
      if (kind === 'file') {
        rec.warn = h('span', { class: 'mm-quote__warn', id: id + '-warn' });
        wrap.insertBefore(rec.warn, err);
        el.setAttribute('aria-describedby', hintId + ' ' + id + '-warn ' + errId);
        el.addEventListener('change', () => this.onFiles(rec));
      }
      const evt = kind === 'select' || kind === 'file' ? 'change' : 'input';
      el.addEventListener(evt, () => { if (rec.invalid) this.validateOne(rec); });
      if (fl.id === 'regtext') el.addEventListener('input', () => this.updateRegPreview());
      if (fl.id === 'notes') this.notesRec = rec;
      this.controls[scope + ':' + fl.id] = rec;
      return wrap;
    }

    renderFulfil() {
      const legendId = this.fid('fulfil-legend');
      const noteId = this.fid('fulfil-note');
      this.installNote = h('p', { class: 'mm-quote__hint mm-quote__install-note', id: noteId, hidden: true, text: INSTALL_NOTE });
      const opts = h('div', { class: 'mm-quote__radios' });
      this.fulfilInputs = FULFIL.opts.map(([v, l], i) => {
        const inp = h('input', { type: 'radio', name: this.fid('fulfil'), value: v, checked: i === 0, 'aria-describedby': noteId, onchange: () => this.onFulfil() });
        opts.appendChild(h('label', { class: 'mm-quote__radio' }, inp, ' ' + l));
        return inp;
      });
      return h('fieldset', { class: 'mm-quote__field mm-quote__field--full mm-quote__fieldset' },
        h('legend', { class: 'mm-quote__label', id: legendId, text: FULFIL.label }), opts, h('div', { 'aria-live': 'polite' }, this.installNote));
    }

    onFulfil() {
      const v = this.fulfilValue();
      this.installNote.hidden = v !== 'install';
    }
    fulfilValue() {
      const c = this.fulfilInputs.find((i) => i.checked);
      return c ? c.value : 'ship';
    }

    renderCheck(which, text) {
      const id = this.fid(which);
      const errId = id + '-err';
      const input = h('input', { type: 'checkbox', id, class: 'mm-quote__checkbox', 'aria-describedby': errId, 'aria-required': 'true' });
      const err = h('span', { class: 'mm-quote__err', id: errId });
      const wrap = h('div', { class: 'mm-quote__check' },
        h('label', { class: 'mm-quote__check-label', for: id }, input, h('span', null, text, h('span', { class: 'mm-quote__req', 'aria-hidden': 'true', text: ' *' }))), err);
      const rec = { def: { id: which, kind: 'check', req: true }, el: input, wrap, err, scope: 'check' };
      input.addEventListener('change', () => { if (rec.invalid) this.validateOne(rec); });
      this.controls['check:' + which] = rec;
      return wrap;
    }

    renderRegPreview() {
      this.regCount = h('span', { class: 'mm-quote__hint', 'aria-live': 'polite' });
      this.regText = h('span', { class: 'mm-quote__regtext', 'aria-hidden': 'true' });
      this.regPreview = h('div', { class: 'mm-quote__regpreview mm-quote__field--full', hidden: true },
        h('span', { class: 'mm-quote__eyebrow', text: 'Check your characters' }), this.regText, this.regCount);
      return this.regPreview;
    }

    updateRegPreview() {
      const rec = this.controls['registration:regtext'];
      if (!rec || !this.regPreview) return;
      const rt = String(rec.el.value || '').toUpperCase();
      const chars = rt.replace(/\s/g, '').length;
      this.regPreview.hidden = !rt.trim();
      this.regText.textContent = rt;
      this.regCount.textContent = rt.trim() ? chars + (chars === 1 ? ' character' : ' characters') + ' (spaces not counted)' : '';
    }

    renderDesign() {
      const d = this.design;
      const rows = [
        ['Name', d.name],
        ['Hailing port', d.port || '—'],
        ['Material', d.material ? (d.material === 'acrylic' ? 'Dimensional acrylic' : 'Cut vinyl') : null],
        ['Style', d.style ? STYLE_LABELS[d.style] : null],
        ['Finish', d.finish ? (FINISH_LABELS[d.finish] || d.finish) : null],
        ['Letter height', d.height_in != null ? d.height_in.toFixed(1) + ' in' : null],
        ['Approx. width', d.approx_width_in != null ? d.approx_width_in.toFixed(1) + ' in' : null],
        ['Transom', d.transom_in != null ? d.transom_in + ' in' : null]
      ].filter((r) => r[1]);
      const dl = h('dl', { class: 'mm-quote__design-list' }, rows.map(([k, v]) => [h('dt', { text: k }), h('dd', { text: v })]));
      let edit = null;
      if (this.studioUrl) {
        const p = new URLSearchParams();
        p.set('name', d.name);
        if (d.port) p.set('port', d.port);
        if (d.material) p.set('material', d.material);
        if (d.style) p.set('style', d.style);
        if (d.finish) p.set('finish', d.finish);
        if (d.height_in != null) p.set('height', String(d.height_in));
        if (d.transom_in != null) p.set('transom', String(d.transom_in));
        edit = h('a', { class: 'mm-quote__studio-link', href: this.studioUrl + (this.studioUrl.includes('?') ? '&' : '?') + p.toString(), text: 'Edit design' });
      }
      return h('section', { class: 'mm-quote__design', 'aria-label': 'Your design' },
        h('span', { class: 'mm-quote__eyebrow', text: 'Design carried from the studio' }), dl, edit);
    }

    /* ---------- Type switching ---------- */
    setType(t, focus) {
      if (!TYPES.includes(t) || t === this.type || this.status === 'loading') return;
      this.type = t;
      this.clearErrors();
      this.status = 'idle';
      this.failEl.hidden = true;
      this.applyType(focus);
    }

    applyType(focus) {
      const d = DEFS[this.type];
      Object.keys(this.typeGroups).forEach((k) => {
        const g = this.typeGroups[k];
        const on = k === this.type;
        g.hidden = !on;
        g.querySelectorAll('input, select, textarea').forEach((el) => { el.disabled = !on; });
      });
      if (this.pickerBtns) {
        Object.keys(this.pickerBtns).forEach((k) => this.pickerBtns[k].setAttribute('aria-pressed', String(k === this.type)));
        this.typeHeadingEl.textContent = d.heading;
        this.typeIntroEl.textContent = d.intro;
        this.typeNoteEl.textContent = d.note || '';
        this.typeNoteEl.hidden = !d.note;
        if (this.studioLinkEl) this.studioLinkEl.hidden = this.type !== 'name' || !!this.design;
      }
      if (this.designEl) this.designEl.hidden = this.type !== 'name';
      if (this.design && this.type === 'name') this.prefillFromDesign();
      if (this.notesRec) this.notesRec.el.placeholder = d.notesPh;
      this.confirmWrap.hidden = this.type !== 'registration';
      this.controls['check:confirm'].el.disabled = this.type !== 'registration';
      this.submitBtn.textContent = this.showPicker ? 'Send request' : d.submit;
      this.updateRegPreview();
      if (focus) this.announce(d.heading);
    }

    prefillFromDesign() {
      const n = this.controls['name:bname'], p = this.controls['name:port'];
      if (n && !n.el.value) n.el.value = this.design.name;
      if (p && !p.el.value && this.design.port) p.el.value = this.design.port;
    }

    /* ---------- Files ---------- */
    onFiles(rec) {
      const files = Array.from(rec.el.files || []);
      rec.hint.textContent = files.length ? 'Attached: ' + files.map((f) => f.name).join(', ') : (rec.def.hint || '');
      const big = files.filter((f) => f.size > this.maxMb * 1024 * 1024);
      rec.warn.textContent = big.length
        ? (big.length === 1 ? big[0].name + ' is' : big.length + ' files are') + ' larger than ' + this.maxMb + ' MB and may not upload. A smaller copy is fine to start.'
        : '';
    }

    /* ---------- Validation (ported from Quote.dc.html validate()) ---------- */
    activeRecs() {
      const list = [];
      COMMON_HEAD.forEach((fl) => list.push(this.controls['common:' + fl.id]));
      this.fieldsFor(this.type).forEach((fl) => list.push(this.controls[this.type + ':' + fl.id]));
      list.push(this.controls['common:notes']);
      if (this.type === 'registration') list.push(this.controls['check:confirm']);
      list.push(this.controls['check:consent']);
      return list.filter(Boolean);
    }

    errorFor(rec) {
      const fl = rec.def, el = rec.el;
      if (fl.kind === 'check') {
        if (!el.checked) return fl.id === 'confirm' ? MSG.confirm : MSG.consent;
        return '';
      }
      if (fl.kind === 'file') return fl.req && !(el.files && el.files.length) ? MSG.file : '';
      const v = String(el.value || '').trim();
      if (fl.req && !v) return fl.kind === 'select' ? MSG.select : MSG.required;
      if (fl.type === 'email' && v && !EMAIL_RE.test(v)) return MSG.email;
      return '';
    }

    setError(rec, msg) {
      rec.invalid = !!msg;
      rec.err.textContent = msg;
      if (msg) rec.el.setAttribute('aria-invalid', 'true');
      else rec.el.removeAttribute('aria-invalid');
      rec.wrap.classList.toggle('is-invalid', !!msg);
    }

    validateOne(rec) {
      this.setError(rec, this.errorFor(rec));
      this.updateSummary();
    }

    updateSummary() {
      const n = this.activeRecs().filter((r) => r.invalid).length;
      if (!n) { this.summaryEl.hidden = true; this.summaryEl.textContent = ''; return; }
      this.summaryEl.hidden = false;
      this.summaryEl.textContent = n === 1 ? 'Please fix 1 field below.' : 'Please fix ' + n + ' fields below.';
    }

    clearErrors() {
      Object.keys(this.controls).forEach((k) => this.setError(this.controls[k], ''));
      this.summaryEl.hidden = true;
      this.summaryEl.textContent = '';
    }

    validateAll() {
      const bad = [];
      this.activeRecs().forEach((rec) => {
        const msg = this.errorFor(rec);
        this.setError(rec, msg);
        if (msg) bad.push(rec);
      });
      this.updateSummary();
      return bad;
    }

    /* ---------- Submit ---------- */
    submit() {
      if (this.status === 'loading') return;
      this.failEl.hidden = true;
      const bad = this.validateAll();
      if (bad.length) {
        // Summary is a role="alert" region (announced); focus goes to the first invalid field.
        const first = bad[0].el;
        window.setTimeout(() => {
          first.focus({ preventScroll: true });
          first.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
        }, 0);
        return;
      }
      this.setLoading(true);
      const fd = this.buildPayload();
      if (this.simFail) {
        window.setTimeout(() => this.finish(false), 1000);
      } else if (this.demo) {
        window.setTimeout(() => this.finish(true, 'DEMO'), 1000);
      } else if (this.mode === 'shopify') {
        this.sendShopify();
      } else {
        this.send(fd);
      }
    }

    buildPayload() {
      const fd = new FormData();
      fd.append('type', this.type);
      this.activeRecs().forEach((rec) => {
        const fl = rec.def;
        if (fl.kind === 'check') return;
        if (fl.kind === 'file') {
          Array.from(rec.el.files || []).forEach((f) => fd.append('files[]', f, f.name));
          return;
        }
        const v = String(rec.el.value || '').trim();
        if (v) fd.append(fl.key, v);
      });
      fd.append('fulfilment', this.fulfilValue());
      if (this.type === 'registration') fd.append('requirements_confirmed', 'true');
      if (this.design && this.type === 'name') fd.append('design', JSON.stringify(this.design));
      fd.append('consent', 'true');
      fd.append('consent_text', CONSENT_TEXT);
      fd.append('page_url', window.location.origin + window.location.pathname);
      return fd;
    }

    async send(fd) {
      let ok = false, ref = '';
      const ctrl = 'AbortController' in window ? new AbortController() : null;
      const timer = ctrl ? window.setTimeout(() => ctrl.abort(), 60000) : null;
      try {
        const res = await fetch(this.endpoint, {
          method: 'POST', body: fd, credentials: 'same-origin',
          headers: { Accept: 'application/json' }, signal: ctrl ? ctrl.signal : undefined
        });
        const data = await res.json().catch(() => null);
        if (res.ok && data && data.ok === true) {
          ok = true;
          ref = typeof data.reference === 'string' || typeof data.reference === 'number' ? String(data.reference).slice(0, 64) : '';
        }
      } catch (e) {
        ok = false;
      } finally {
        if (timer) window.clearTimeout(timer);
      }
      this.finish(ok, ref);
    }

    /** Copy the validated request into the native contact form and submit it (full page post). */
    sendShopify() {
      const f = this.contactForm;
      const val = (rec) => String(rec.el.value || '').trim();
      const set = (name, v) => { const i = f.querySelector('[name="' + name + '"]'); if (i) i.value = v; };
      const lines = [];
      this.activeRecs().forEach((rec) => {
        const fl = rec.def;
        if (fl.kind === 'check' || fl.kind === 'file') return;
        if (rec.scope === 'common' && (fl.key === 'name' || fl.key === 'email' || fl.key === 'phone')) return;
        const v = val(rec);
        if (v) lines.push(fl.label + ': ' + v);
      });
      const fulfil = FULFIL.opts.find(([v]) => v === this.fulfilValue());
      lines.push(FULFIL.label + ' ' + (fulfil ? fulfil[1] : this.fulfilValue()));
      if (this.type === 'registration') lines.push('Requirements confirmed: ' + DEFS.registration.confirm);
      if (this.design && this.type === 'name') {
        const d = this.design;
        lines.push('Studio design: ' + [d.name, d.port, d.material, d.style && STYLE_LABELS[d.style], d.finish && (FINISH_LABELS[d.finish] || d.finish),
          d.height_in != null && d.height_in + ' in letters', d.approx_width_in != null && '≈' + d.approx_width_in + ' in wide',
          d.transom_in != null && d.transom_in + ' in transom'].filter(Boolean).join(' · '));
      }
      lines.push('Consent: ' + CONSENT_TEXT);
      lines.push('Page: ' + window.location.origin + window.location.pathname);
      set('contact[name]', val(this.controls['common:name']));
      set('contact[email]', val(this.controls['common:email']));
      set('contact[phone]', this.controls['common:phone'] ? val(this.controls['common:phone']) : '');
      set('contact[Request type]', DEFS[this.type].title);
      set('contact[body]', lines.join('\n'));
      HTMLFormElement.prototype.submit.call(f);
    }

    setLoading(on) {
      this.status = on ? 'loading' : 'idle';
      this.submitBtn.setAttribute('aria-disabled', on ? 'true' : 'false');
      this.submitBtn.textContent = on ? 'Sending…' : (this.showPicker ? 'Send request' : DEFS[this.type].submit);
      this.form.setAttribute('aria-busy', on ? 'true' : 'false');
      if (this.pickerBtns) Object.values(this.pickerBtns).forEach((b) => { b.disabled = on; });
      if (on) this.announce('Sending…');
    }

    finish(ok, ref, detail) {
      this.setLoading(false);
      if (!ok) {
        this.status = 'error';
        this.failEl.textContent = '';
        this.failEl.append(h('strong', { text: MSG.failTitle }), ' ', detail ? detail + ' Please try again.' : MSG.failBody);
        if (this.demo || this.simFail) this.failEl.append(' ', h('span', { class: 'mm-quote__demo-tag', text: '(Demo failure state.)' }));
        this.failEl.hidden = false;
        this.failEl.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
        return;
      }
      this.status = 'success';
      const d = DEFS[this.type];
      const s = this.successEl;
      s.textContent = '';
      const title = h('p', { class: 'mm-quote__success-title', tabindex: '-1' });
      if (this.demo) {
        title.textContent = this.showPicker ? 'Your ' + d.title.toLowerCase() + ' request is ready to send.' : d.success;
        s.append(
          h('p', { class: 'mm-quote__eyebrow mm-quote__eyebrow--success', text: 'Demo: nothing was sent' }),
          title,
          h('p', { text: 'This demo form is not connected to email, uploads or CRM. Reference: DEMO' })
        );
      } else {
        title.textContent = 'MarineMaker received your request.';
        s.append(title);
        if (ref) s.append(h('p', null, 'Reference: ', h('strong', { text: ref })));
        if (this.mode === 'shopify') s.append(h('p', { text: SENT_NOTE }));
      }
      s.append(h('button', { type: 'button', class: 'btn btn--primary mm-quote__reset', text: this.showPicker ? 'Start another request' : d.reset, onclick: () => this.reset() }));
      s.hidden = false;
      this.form.hidden = true;
      this.announce(title.textContent);
      title.focus({ preventScroll: true });
      s.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
    }

    reset() {
      this.form.reset();
      Object.keys(this.controls).forEach((k) => {
        const r = this.controls[k];
        if (r.def.kind === 'file') { r.hint.textContent = r.def.hint || ''; r.warn.textContent = ''; }
      });
      this.clearErrors();
      this.onFulfil();
      this.status = 'idle';
      this.failEl.hidden = true;
      this.successEl.hidden = true;
      this.form.hidden = false;
      this.applyType(false);
      const first = this.controls['common:name'];
      if (first) first.el.focus();
    }

    announce(text) {
      this.liveEl.textContent = '';
      window.setTimeout(() => { this.liveEl.textContent = text; }, 50);
    }
  }

  customElements.define('mm-quote-request', MMQuoteRequest);
})();
